<script setup lang="ts">
import Phone from '@/components/views/helpee-signup-simple/phone.vue';
import Code from '@/components/views/helpee-signup-simple/code.vue';
import Needs from '@/components/views/helpee-signup-simple/needs.vue';
import TouchPoints from '@/components/views/helpee-signup-simple/touchpoints.vue';
import Finish from '@/components/views/helpee-signup-simple/finish.vue';
import { computed, onMounted, reactive } from 'vue';
import { ref } from 'vue';
import { useRoute } from 'vue-router';
import {
  findError,
  type ErrorKeysType,
} from '@magma-app/magma-errors/dist/index';
import { useHelpeeData } from '@/api/complete-helpee-signup/use-helpee-data';
import { useAuthStore } from '@/stores/auth';
import { useResendTimer } from '@/composables/useResendTimer';
import { localizeErrorMessage } from '@/services/http';

const authStore = useAuthStore();
const route = useRoute();

const { data, isLoading, error: loadError } = useHelpeeData(
  route.params.campaignHelpeeUuid as string | undefined
);

// The link cannot start a sign-up (request closed, campaign archived...): explain it instead of the form
const loadErrorMessage = computed(() => {
  const response = (loadError.value as any)?.response?.data;
  if (!response?.type || !response?.subtype) return null;
  return localizeErrorMessage(
    findError(response.type as ErrorKeysType, response.subtype).message
  ) || null;
});

// Phone verification resend timer
const {
  canResend: canSendCode,
  timerBeforeResend,
  startTimer,
  resetTimer,
  handleResend,
  isResending: isResendingCode,
} = useResendTimer();

const currentStep = ref<'phone' | 'code' | 'needs' | 'touchpoints' | 'finish'>(
  'phone'
);
const isSubmittingPhone = ref(false);
const isSubmittingTouchpoints = ref(false);
const isSubmittingNeeds = ref(false);
const infos = reactive({
  phone: '',
  accept: false,
  criterias: [] as { id: string; answerKey?: string; answerKeys?: string[] }[],
  expectation: '',
  touchPoints: [] as any[],
  acceptMail: true,
  acceptPhone: false,
});

const handlePhoneSubmit = async (d: { phone: string; accept: boolean }) => {
  if (isSubmittingPhone.value) {
    return;
  }

  infos.phone = d.phone;
  infos.accept = d.accept;

  isSubmittingPhone.value = true;

  try {
    // Only send code if timer allows (prevents rapid retries when going back/forward)
    if (canSendCode.value) {
      await authStore.sendCodeRequest({
        phoneNumber: d.phone,
        prospectUuid: route.params.campaignHelpeeUuid as string,
      });
      startTimer();
    }

    currentStep.value = 'code';
  } catch (error: any) {
    // link reopened after the phone step was completed: nothing left to verify
    if (error?.response?.data?.subtype === 'PHONE-NUMBER-ALREADY-VERIFIED') {
      resetTimer();
      currentStep.value = 'needs';
      return;
    }
    throw error;
  } finally {
    isSubmittingPhone.value = false;
  }
};

const handleResendCode = async () => {
  await handleResend(async () => {
    await authStore.sendCodeRequest({
      phoneNumber: infos.phone,
      prospectUuid: route.params.campaignHelpeeUuid as string,
    });
  });
};

const handleCodeSubmit = async () => {
  // Reset timer when code is successfully verified
  resetTimer();
  currentStep.value = 'needs';
};

const handleNeedsSubmit = async (d: {
  criterias: { id: string; answerKey?: string; answerKeys?: string[] }[];
  expectation: string;
}) => {
  if (isSubmittingNeeds.value) {
    return;
  }

  infos.criterias = d.criterias;
  infos.expectation = d.expectation;

  isSubmittingNeeds.value = true;

  try {
    await authStore.addOptionalInfoSignUpHelpee({
      expectation: d.expectation,
      prospectCriteria: d.criterias.map((c) => ({
        [`${c.answerKey ? 'answerKey' : 'answerKeys'}`]: c.answerKey
          ? c.answerKey
          : c.answerKeys,
        criteriaId: Number(c.id),
      })),
      prospectUuid: (route.params.campaignHelpeeUuid as string) || '',
    });

    currentStep.value = 'touchpoints';
  } finally {
    isSubmittingNeeds.value = false;
  }
};

const handleTouchPointsSubmit = (d: {
  touchPoints: any[];
  acceptMail: boolean;
  acceptPhone: boolean;
}) => {
  if (isSubmittingTouchpoints.value) {
    return;
  }

  infos.touchPoints = d.touchPoints;
  infos.acceptMail = d.acceptMail;
  infos.acceptPhone = d.acceptPhone;

  isSubmittingTouchpoints.value = true;

  authStore
    .sendTouchPointsSignUpHelpee({
      prospectUuid: route.params.campaignHelpeeUuid as string,
      touchPoints: d.touchPoints,
      acceptEmailNotifications: d.acceptMail,
      acceptPhoneNotifications: d.acceptPhone,
    })
    .then(() => {
      currentStep.value = 'finish';
    })
    .finally(() => {
      isSubmittingTouchpoints.value = false;
    });
};

onMounted(() => {
  const { finish } = route.query;

  if (finish === 'true') {
    currentStep.value = 'finish';
  }
});
</script>

<template>
  <main
    v-if="!isLoading"
    class="flex flex-col bg-background-subtle lg:justify-center lg:items-center"
  >
    <div
      class="bg-white rounded-lg lg:shadow-lg w-full lg:w-[480px] h-dvh lg:h-auto flex flex-col"
    >
      <div
        class="h-16 flex items-center gap-2 px-10 border-b border-border-subtle"
      >
        <img
          :src="data?.signUpInformation?.logoUrl"
          :alt="data?.signUpInformation?.name"
          class="rounded-full h-8 w-8ain"
        />
        <p class="text-mgm-txt-sm text-foreground-emphasis font-medium">
          {{ data?.signUpInformation?.name }}
        </p>
      </div>
      <p
        v-if="loadErrorMessage"
        class="px-10 py-8 text-mgm-txt-sm text-foreground-default"
      >
        {{ loadErrorMessage }}
      </p>
      <template v-else>
        <Phone
          v-if="currentStep === 'phone'"
          :data="data"
          :is-loading="isSubmittingPhone"
          @submit="handlePhoneSubmit"
        />
        <Code
          v-if="currentStep === 'code'"
          :can-resend="canSendCode"
          :timer-before-resend="timerBeforeResend"
          :is-resending="isResendingCode"
          @back="currentStep = 'phone'"
          @submit="handleCodeSubmit"
          @resend="handleResendCode"
        />
        <Needs
          v-if="currentStep === 'needs'"
          :is-loading="isSubmittingNeeds"
          @submit="handleNeedsSubmit"
        />
        <TouchPoints
          v-if="currentStep === 'touchpoints'"
          :email="data?.signUpInformation?.email || ''"
          :phone-number="infos.phone"
          :disable-email="false"
          :is-loading="isSubmittingTouchpoints"
          @submit="handleTouchPointsSubmit"
        />
        <Finish v-if="currentStep === 'finish'" />
      </template>
    </div>
    <a
      class="mx-auto mt-8 text-center text-mgm-txt-xs font-normal text-foreground-default hover:text-foreground-default focus:text-foreground-default hidden lg:flex items-center"
      href="https://www.magma.app/"
      target="_blank"
    >
      {{ $t('generic.powered') }}
      <img class="ml-2" src="/img/logo_magma_full_bw.svg" />
    </a>
  </main>
</template>
